"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
    Lock,
    Coins,
    CheckCircle2,
    Timer,
    Wallet,
    Sparkles,
    RefreshCcw,
    Copy,
    ExternalLink,
} from "lucide-react"
import Link from "next/link"
import { useState, useEffect } from "react"
import { useWallet } from "@/lib/wallet-context"
import { useTokenLocker, useTokenApproval, ERC20_ABI } from "@/lib/contract-hooks"
import { useChainId, useSwitchChain, useReadContracts, useReadContract } from "wagmi"
import { formatUnits, parseUnits } from "viem"
import { toast } from "sonner"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"

// Copy Button Component
function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <button
            onClick={handleCopy}
            className="p-1 rounded hover:bg-muted transition-colors"
            title="Copy"
        >
            {copied ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
            ) : (
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
            )}
        </button>
    )
}

// Core tokens to check for each network
const CORE_TOKENS: Record<number, { address: string; symbol: string; name: string; isPlatform?: boolean }[]> = {
    // BSC Testnet
    97: [
        { address: "0x337610d27c682E347C9cD60BD4b3b107C9d34dDd", symbol: "USDT", name: "Tether USD", isPlatform: false },
        { address: "0x64544969ed7EBf5f083679233325356EbE738930", symbol: "USDC", name: "USD Coin", isPlatform: false },
        { address: "0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd", symbol: "WBNB", name: "Wrapped BNB", isPlatform: false },
    ],
    // Sepolia
    11155111: [
        { address: "0x779877A7B0D9E8603169DdbD7836e478b4624789", symbol: "LINK", name: "Chainlink", isPlatform: false },
        { address: "0x94a9D9AC8a22534E3FaCa9F4e7F2E2cf85d5E4C8", symbol: "USDC", name: "USD Coin", isPlatform: false },
        { address: "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984", symbol: "UNI", name: "Uniswap", isPlatform: false },
    ],
    // Polygon Amoy
    80002: [
        { address: "0x41e94eb019c0762f9bfcf9fb1e58725bfb0e7582", symbol: "USDC", name: "USD Coin", isPlatform: false },
        { address: "0x1fdE0f8f9D6069E17cfAcf7a8e560f76002A91D9", symbol: "USDT", name: "Tether USD", isPlatform: false },
    ],
    // Base Sepolia
    84532: [
        { address: "0x036CbD53842c5426634e7929541eC2318f3dCF7e", symbol: "USDC", name: "USD Coin", isPlatform: false },
        { address: "0x4200000000000000000000000000000000000006", symbol: "WETH", name: "Wrapped Ether", isPlatform: false },
    ]
}

export function CreateLockForm() {
    const [tokenAddress, setTokenAddress] = useState("")
    const [amount, setAmount] = useState("")
    const [unlockDate, setUnlockDate] = useState("")
    const [isVesting, setIsVesting] = useState(false)
    const [cliffDays, setCliffDays] = useState("0")
    const [vestingDays, setVestingDays] = useState("30")
    const [isLP, setIsLP] = useState(false)
    const [selectionMode, setSelectionMode] = useState<"address" | "wallet">("address")
    const [candidateTokens, setCandidateTokens] = useState<any[]>([])
    const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)

    const { isConnected, connectWallet, address } = useWallet()
    const chainId = useChainId()
    const { chains, switchChain } = useSwitchChain()
    const { lockTokens, createVestingLock, lockFee, contractId, isPending: isLockPending, isConfirming, isSuccess, hash } = useTokenLocker(chainId)
    const lockerAddress = useTokenLocker(chainId).contractAddress
    const { approve, isPending: isApprovePending, isSuccess: isApproveSuccess } = useTokenApproval(tokenAddress, lockerAddress || "")

    // Wallet Scan Logic
    const [walletTokens, setWalletTokens] = useState<any[]>([])
    const [scanWarning, setScanWarning] = useState<string | null>(null)

    useEffect(() => {
        if (isConnected && chainId && address) {
            startWalletScan()
        }
    }, [isConnected, chainId, address])

    const startWalletScan = async () => {
        setIsLoadingCandidates(true)
        setScanWarning(null)
        try {
            const ankrRes = await fetch(`/api/wallet/tokens?address=${address}&chainId=${chainId}`)
            const ankrData = await ankrRes.json()

            // Show warning if API returned one
            if (ankrData.warning) {
                setScanWarning(ankrData.warning)
            }

            const platformRes = await fetch(`/api/tokens?chainId=${chainId}`)
            const platformTokens = await platformRes.json()
            const platformAddresses = new Set(
                Array.isArray(platformTokens)
                    ? platformTokens.map((t: any) => t.address.toLowerCase())
                    : []
            )

            const tokens = (ankrData.tokens || []).map((token: any) => ({
                ...token,
                isPlatform: platformAddresses.has(token.address.toLowerCase())
            }))

            tokens.sort((a: any, b: any) => {
                if (a.isPlatform !== b.isPlatform) return a.isPlatform ? -1 : 1
                return (parseFloat(b.balanceUsd) || 0) - (parseFloat(a.balanceUsd) || 0)
            })

            // If we got wallet tokens, use them. Otherwise fall back to core + platform tokens.
            if (tokens.length > 0) {
                setWalletTokens(tokens)
            } else {
                // Populate candidate tokens with core tokens + platform tokens for on-chain balance checking
                const coreForChain = CORE_TOKENS[chainId] || []
                const merged = [...coreForChain]
                if (Array.isArray(platformTokens)) {
                    platformTokens.forEach((pt: any) => {
                        if (!merged.find(m => m.address.toLowerCase() === pt.address.toLowerCase())) {
                            merged.push({ address: pt.address, symbol: pt.symbol, name: pt.name, isPlatform: true })
                        }
                    })
                }
                setCandidateTokens(merged)
            }
        } catch (error) {
            console.error("Error during wallet scan:", error)
            setScanWarning("Wallet scan failed. You can enter a token address manually.")
            try {
                const res = await fetch(`/api/tokens?chainId=${chainId}`)
                const platformTokens = await res.json()
                const coreForChain = CORE_TOKENS[chainId] || []
                const merged = [...coreForChain]
                if (Array.isArray(platformTokens)) {
                    platformTokens.forEach((pt: any) => {
                        if (!merged.find(m => m.address.toLowerCase() === pt.address.toLowerCase())) {
                            merged.push({ address: pt.address, symbol: pt.symbol, name: pt.name, isPlatform: true })
                        }
                    })
                }
                setCandidateTokens(merged)
            } catch {
                // Last resort: just show core tokens
                setCandidateTokens(CORE_TOKENS[chainId] || [])
            }
        } finally {
            setIsLoadingCandidates(false)
        }
    }

    const { data: balanceResults, isLoading: isCheckingBalances } = useReadContracts({
        contracts: candidateTokens.map(t => ({
            address: t.address as `0x${string}`,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [address as `0x${string}`],
        })),
        query: {
            enabled: isConnected && candidateTokens.length > 0 && !!address && walletTokens.length === 0
        }
    })

    const userTokensWithBalance = walletTokens.length > 0
        ? walletTokens
        : candidateTokens
            .map((token, index) => {
                const balance = balanceResults?.[index]?.result as bigint | undefined
                return { ...token, balance: balance || BigInt(0) }
            })
            .filter(t => t.balance > BigInt(0))
            .sort((a, b) => (a.isPlatform === b.isPlatform ? 0 : a.isPlatform ? -1 : 1))

    const { data: name } = useReadContract({ address: tokenAddress as `0x${string}`, abi: ERC20_ABI, functionName: "name" })
    const { data: symbol } = useReadContract({ address: tokenAddress as `0x${string}`, abi: ERC20_ABI, functionName: "symbol" })
    const { data: decimals } = useReadContract({ address: tokenAddress as `0x${string}`, abi: ERC20_ABI, functionName: "decimals" })
    const { data: userBalance } = useReadContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "balanceOf",
        args: [address as `0x${string}`],
        query: { enabled: !!address && !!tokenAddress && tokenAddress.length === 42 }
    })
    const { data: allowance, refetch: refetchAllowance } = useReadContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address as `0x${string}`, lockerAddress as `0x${string}`],
        query: {
            enabled: !!address && !!tokenAddress && !!lockerAddress,
            refetchInterval: 5000 // Poll every 5 seconds for safety
        }
    })

    // Refetch allowance when approval is successful
    useEffect(() => {
        if (isApproveSuccess) {
            refetchAllowance()
        }
    }, [isApproveSuccess, refetchAllowance])

    const tokenInfo = name && symbol ? {
        name: name as string,
        symbol: symbol as string,
        decimals: Number(decimals || 18),
        balance: userBalance ? formatUnits(userBalance as bigint, Number(decimals || 18)) : "0"
    } : null

    const needsApproval = tokenInfo && amount && allowance !== undefined ?
        (allowance as bigint) < parseUnits(amount, tokenInfo.decimals) : true

    const handleLock = async () => {
        if (!tokenInfo || !amount || (!unlockDate && !isVesting)) return

        try {
            const amountBigInt = parseUnits(amount, tokenInfo.decimals)
            if (isVesting) {
                const start = Math.floor(Date.now() / 1000)
                await createVestingLock({
                    tokenAddress,
                    amount: amountBigInt,
                    startTime: start,
                    cliffDuration: Number(cliffDays) * 86400,
                    vestingDuration: Number(vestingDays) * 86400,
                    unlockTime: start + Number(vestingDays) * 86400,
                    isLP
                })
            } else {
                const unlock = Math.floor(new Date(unlockDate).getTime() / 1000)
                await lockTokens({
                    tokenAddress,
                    amount: amountBigInt,
                    unlockTime: unlock,
                    isLP
                })
            }
        } catch (err) {
            console.error(err)
            toast.error("Failed to initiate lock transaction")
        }
    }

    const [isLocked, setIsLocked] = useState(false)
    const [txDetails, setTxDetails] = useState<any>(null)

    useEffect(() => {
        if (isSuccess && hash && !isLocked) {
            recordLockToDb()
        }
    }, [isSuccess, hash, contractId])

    const recordLockToDb = async () => {
        try {
            const lockData = {
                contractId: contractId?.toString() || "0",
                tokenAddress,
                ownerAddress: address,
                amount,
                unlockDate: isVesting
                    ? new Date(Date.now() + Number(vestingDays) * 86400000).toISOString()
                    : new Date(unlockDate).toISOString(),
                isLP,
                txHash: hash,
                chainId,
                tokenName: tokenInfo?.name,
                tokenSymbol: tokenInfo?.symbol,
                tokenDecimals: tokenInfo?.decimals
            }

            const res = await fetch('/api/locks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(lockData)
            })

            if (res.ok) {
                const recordedLock = await res.json()
                setTxDetails(recordedLock)
                setIsLocked(true)
                toast.success("Lock recorded successfully!")
            }
        } catch (err) {
            console.error("Failed to sync lock to DB:", err)
            toast.error("Lock created on-chain but failed to save to database.")
            // Still show success UI if hash exists
            setIsLocked(true)
        }
    }

    if (isLocked) {
        return (
            <Card className="max-w-2xl mx-auto shadow-2xl border-primary/20 overflow-hidden animate-in fade-in zoom-in duration-500">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                <CardContent className="p-8 text-center space-y-6 relative">
                    <div className="flex justify-center">
                        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center animate-bounce">
                            <CheckCircle2 className="h-10 w-10 text-primary" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h2 className="text-3xl font-bold tracking-tight">Lock Created Successfully!</h2>
                        <p className="text-muted-foreground">
                            Your {isLP ? 'LP ' : ''}tokens have been securely locked on-chain.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-muted/30 border border-primary/10 text-left">
                        <div>
                            <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Locked Amount</div>
                            <div className="font-mono font-bold text-lg">{amount} {tokenInfo?.symbol}</div>
                        </div>
                        <div>
                            <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Unlock Date</div>
                            <div className="font-mono font-bold text-lg">
                                {isVesting ? "Vesting Schedule" : new Date(unlockDate).toLocaleDateString()}
                            </div>
                        </div>
                        <div className="col-span-2">
                            <div className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Transaction Hash</div>
                            <div className="flex items-center gap-2 font-mono text-xs text-primary truncate bg-primary/5 p-2 rounded-md mt-1">
                                <span>{hash}</span>
                                <CopyButton text={hash || ""} />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <Button asChild className="flex-1 h-12 text-base font-bold shadow-lg shadow-primary/20">
                            <Link href={`/lock/${txDetails?.id || ''}`}>
                                View Lock Details
                                <ExternalLink className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                        <Button variant="outline" asChild className="flex-1 h-12 text-base font-bold">
                            <Link href="/lock">
                                Back to All Locks
                            </Link>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        )
    }

    const presetDurations = [
        { label: "1 Month", days: 30 },
        { label: "3 Months", days: 90 },
        { label: "6 Months", days: 180 },
        { label: "1 Year", days: 365 },
    ]

    return (
        <Card className="max-w-2xl mx-auto shadow-xl border-primary/10">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                    <Lock className="h-6 w-6 text-primary" />
                    Create Token Lock
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="flex flex-col gap-4 p-1 rounded-lg bg-muted/50">
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsLP(false)}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${!isLP ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            Standard Token
                        </button>
                        <button
                            onClick={() => setIsLP(true)}
                            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${isLP ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            LP Token
                        </button>
                    </div>

                    <div className="flex flex-col gap-2 px-2 pb-2 border-t border-border/50 pt-3">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Network</span>
                        <div className="flex flex-wrap gap-2">
                            {chains.map((chain) => (
                                <button
                                    key={chain.id}
                                    onClick={() => switchChain({ chainId: chain.id })}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${chainId === chain.id ? 'bg-primary border-primary text-primary-foreground' : 'bg-background hover:border-primary/50'}`}
                                >
                                    {chain.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Select Token Source</label>
                        <div className="flex bg-muted p-1 rounded-md text-xs">
                            <button
                                onClick={() => setSelectionMode("address")}
                                className={`px-3 py-1 rounded transition-all ${selectionMode === "address" ? "bg-background shadow-sm font-semibold text-primary" : "text-muted-foreground"}`}
                            >
                                Import Address
                            </button>
                            <button
                                onClick={() => setSelectionMode("wallet")}
                                className={`px-3 py-1 rounded transition-all flex items-center gap-1.5 ${selectionMode === "wallet" ? "bg-background shadow-sm font-semibold text-primary" : "text-muted-foreground"}`}
                            >
                                <Sparkles className="h-3 w-3" />
                                My Wallet
                            </button>
                        </div>
                    </div>

                    {selectionMode === "address" ? (
                        <div className="space-y-2">
                            <Input
                                placeholder="0x..."
                                value={tokenAddress}
                                onChange={(e) => setTokenAddress(e.target.value)}
                                className="font-mono h-11"
                            />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Select onValueChange={(val) => setTokenAddress(val)}>
                                <SelectTrigger className="w-full h-11">
                                    <SelectValue placeholder={
                                        isLoadingCandidates || isCheckingBalances
                                            ? "Scanning wallet..."
                                            : selectionMode === "wallet" && userTokensWithBalance.length === 0
                                                ? "No tokens found with balance"
                                                : "Select a token you hold"
                                    } />
                                </SelectTrigger>
                                <SelectContent>
                                    {userTokensWithBalance.map((t) => (
                                        <SelectItem key={t.address} value={t.address}>
                                            <div className="flex items-center justify-between w-full min-w-[200px]">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold">{t.symbol}</span>
                                                    <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">{t.name}</span>
                                                    {t.isPlatform && <Badge className="h-4 text-[8px] bg-primary/20 text-primary hover:bg-primary/20 border-0">PLATFORM</Badge>}
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full text-xs text-muted-foreground gap-1.5"
                                onClick={startWalletScan}
                                disabled={isLoadingCandidates}
                            >
                                <RefreshCcw className={`h-3 w-3 ${isLoadingCandidates ? "animate-spin" : ""}`} />
                                {isLoadingCandidates ? "Scanning..." : "Rescan Wallet"}
                            </Button>
                            {scanWarning && (
                                <p className="text-xs text-amber-500/80 text-center px-2">
                                    {scanWarning}
                                </p>
                            )}
                        </div>
                    )}

                    {tokenInfo && (
                        <div className="relative overflow-hidden group p-3 rounded-xl bg-muted/30 border border-primary/10 shadow-sm animate-in fade-in zoom-in duration-300">
                            <div className="relative flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                        <Coins className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-bold">{tokenInfo.symbol}</span>
                                            <span className="text-xs text-muted-foreground truncate max-w-[120px]">{tokenInfo.name}</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/70">
                                            <span>{tokenAddress.slice(0, 8)}...{tokenAddress.slice(-6)}</span>
                                            <CopyButton text={tokenAddress} />
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Balance</div>
                                    <div className="text-lg font-bold font-mono tracking-tight text-foreground">
                                        {Number(tokenInfo.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-medium">Amount to Lock</label>
                    <div className="relative">
                        <Input
                            type="number"
                            placeholder="0.00"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="font-mono h-11 pr-16"
                        />
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10 rounded transition-colors"
                            onClick={() => tokenInfo && setAmount(tokenInfo.balance)}
                        >
                            MAX
                        </button>
                    </div>
                </div>

                {!isVesting ? (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-400">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">Unlock Date</label>
                            <span className="text-[10px] text-muted-foreground font-mono">
                                UTC: {unlockDate || 'YYYY-MM-DD'}
                            </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mb-3">
                            {presetDurations.map((preset) => (
                                <button
                                    key={preset.label}
                                    onClick={() => {
                                        const date = new Date()
                                        date.setDate(date.getDate() + preset.days)
                                        setUnlockDate(date.toISOString().split("T")[0])
                                    }}
                                    className="px-2 py-2.5 text-xs font-medium rounded-xl border border-border/50 hover:border-primary hover:bg-primary/5 transition-all active:scale-95"
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                        <Input
                            type="date"
                            value={unlockDate}
                            onChange={(e) => setUnlockDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                            className="h-11"
                        />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-400">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">Cliff (Days)</label>
                            <Input
                                type="number"
                                value={cliffDays}
                                onChange={(e) => setCliffDays(e.target.value)}
                                className="font-mono h-11"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-muted-foreground">Duration (Days)</label>
                            <Input
                                type="number"
                                value={vestingDays}
                                onChange={(e) => setVestingDays(e.target.value)}
                                className="font-mono h-11"
                            />
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-xl border border-primary/5 bg-primary/5">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg shrink-0 transition-colors ${isVesting ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                            <Timer className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-bold text-sm">Vesting Schedule</div>
                            <div className="text-[11px] text-muted-foreground leading-tight">
                                Release tokens gradually over the specified duration
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsVesting(!isVesting)}
                        className={`w-11 h-6 shrink-0 rounded-full transition-all duration-300 self-end sm:self-auto ${isVesting ? "bg-primary" : "bg-zinc-600"}`}
                    >
                        <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isVesting ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                </div>

                <div className="p-4 rounded-xl border bg-muted/30 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground font-medium">Service Fee</span>
                        <div className="flex items-center gap-1.5 font-mono font-bold text-primary">
                            <span>{lockFee ? formatUnits(lockFee as bigint, 18) : "0.01"}</span>
                            <span className="text-xs">{chainId === 97 ? 'BNB' : 'ETH'}</span>
                        </div>
                    </div>
                </div>

                {!isConnected ? (
                    <Button onClick={connectWallet} className="w-full gap-2 h-12 text-base font-bold" size="lg">
                        <Wallet className="h-5 w-5" />
                        Connect Wallet
                    </Button>
                ) : needsApproval ? (
                    <Button
                        onClick={() => tokenInfo && approve(parseUnits(amount, tokenInfo.decimals))}
                        className="w-full gap-2 h-12 text-base font-bold"
                        size="lg"
                        disabled={!tokenInfo || !amount || isApprovePending}
                    >
                        {isApprovePending ? <RefreshCcw className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                        {isApprovePending ? "Approving..." : `Approve ${tokenInfo?.symbol}`}
                    </Button>
                ) : (
                    <Button
                        onClick={handleLock}
                        className="w-full gap-2 h-12 text-base font-bold shadow-lg shadow-primary/20"
                        size="lg"
                        variant="stargate"
                        disabled={!tokenInfo || !amount || (!unlockDate && !isVesting) || isLockPending || isConfirming}
                    >
                        {isLockPending || isConfirming ? <RefreshCcw className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
                        {isConfirming ? "Confirming..." : (isLockPending ? "Waiting for Wallet..." : "Lock Tokens")}
                    </Button>
                )}
            </CardContent>
        </Card>
    )
}
